import React, { useState, useCallback, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2, CancelCrossIcon, SelectTrigger, InfoBox } from "@activecollab/components";
import classnames from "classnames";
import moment from "moment";
import PropTypes from "prop-types";
import { useSecurity } from "~/components/Hooks";
import { getConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { getProjectById } from "~/store/selectors/selector.projects.jsx";
import { getOpenTasksCountByProjectId, getTaskById } from "~/store/selectors/selector.tasks.jsx";
import { getLoggedUser, getUsersFromProjectMembersForUserSelector } from "~/store/selectors/selector.users.jsx";
import { ExpenseFormContainer } from "./ExpenseFormContainer.js";
import { ExpenseModalContainer } from "./ExpenseModalContainer.js";
import { ExpenseParentTotalContainer } from "./ExpenseParentTotalContainer.js";
import { ListGroupByDate } from "./ListGroupByDate.js";
import { VoteThumbs } from "../ai/VoteThumbs.js";
import useAcSelectDate from "../Hooks/useAcSelectDate.jsx";
import TaskMenuSelector from "../MenuSelector/TaskMenuSelector.jsx";
import { Divider } from "../ModalObjects/Divider.js";
import SelectDateNonWorkingDays from "../SelectDate/SelectDateNonWorkingDays.jsx";

export const ExpenseModalInner = ({
  onClose,
  projectId,
  open,
  taskId: defaultTaskId,
  categoryId,
  descriptionValue,
  initialValue,
  defaultDates,
  attachments,
  showAiInfoBox,
  hideDropDown,
  promptId,
  displaySuccessToast,
}) => {
  const { t } = useTranslation();
  const can = useSecurity();
  const [taskId, setTaskId] = useState(defaultTaskId);
  const [showList, setShowList] = useState(false);
  const showTaskNumber = useSelector((state) => getConfigOption(state, "show_task_id"));
  const showProjectNumber = useSelector((state) => getConfigOption(state, "show_project_id"));
  const task = useSelector((state) => getTaskById(state, taskId));
  const project = useSelector((state) => getProjectById(state, projectId));
  const loggedUser = useSelector((state) => getLoggedUser(state));
  const users = useSelector((state) => getUsersFromProjectMembersForUserSelector(state, projectId));

  const canTrackExpenseForOtherUsers = can(["can_track_expense_for_others"], project);

  const tasksCount = useSelector((state) => getOpenTasksCountByProjectId(state, projectId));

  const { dates, handleDateChange, formatDates } = useAcSelectDate(defaultDates);

  const backtrackMaxDays = useSelector((state) => getConfigOption(state, "backtrack_expense_max_days"));
  const disableDaysBefore = canTrackExpenseForOtherUsers ? null : moment().subtract(backtrackMaxDays, "days");

  const defaultUser = useMemo(() => {
    return {
      id: loggedUser.get("id"),
      name: loggedUser.get("display_name"),
    };
  }, [loggedUser]);

  const filteredUsers = useMemo(() => {
    let result = canTrackExpenseForOtherUsers ? users : [];
    if (result.length === 1 && users[0].id === loggedUser.id) {
      result = [];
    }
    return result;
  }, [canTrackExpenseForOtherUsers, loggedUser.id, users]);

  const handleTaskSelect = useCallback((id) => {
    setTaskId(id);
  }, []);

  const hideFormElements = useMemo(() => {
    if (tasksCount === 0) {
      return !(projectId > 0) && !hideDropDown;
    }
    return !(projectId > 0 && taskId >= 0) && !hideDropDown;
  }, [projectId, taskId, tasksCount, hideDropDown]);

  const handleCancel = useCallback(() => {
    setTaskId(undefined);
  }, [setTaskId]);

  const isBillable = task ? task.is_billable : project.is_billable;

  const showCancel = useMemo(() => {
    if (projectId > 0 && !taskId && tasksCount === 0) {
      return false;
    }
    if (tasksCount > 0 && !defaultTaskId && !hideDropDown) {
      return true;
    }
    return false;
  }, [defaultTaskId, projectId, hideDropDown, taskId, tasksCount]);

  const handleToggleList = useCallback((show) => {
    setShowList(show);
  }, []);

  return (
    <ExpenseModalContainer
      open={open}
      onClose={onClose}
      parentId={taskId > 0 ? taskId : projectId}
      parentType={taskId > 0 ? "Task" : "Project"}
      projectId={projectId}
    >
      <div className="expense-form-modal">
        <div
          className={classnames(`tw-flex tw-justify-between`, {
            "tw-items-start": hideFormElements,
            "tw-items-center": !hideFormElements,
          })}
        >
          {hideFormElements ? (
            <div>
              <TaskMenuSelector
                onTaskSelect={handleTaskSelect}
                projectId={projectId}
              />
            </div>
          ) : (
            <div className="tw-flex tw-items-center tw-truncate tw-mr-4">
              <div className="tw-text-base tw-truncate">
                {!taskId ? (
                  <>
                    <span className="tw-italic">{t("Project")}: </span>
                    <span className="tw-font-bold">
                      {showProjectNumber ? `#${project.project_number}: ${project.name}` : project.name}
                    </span>
                  </>
                ) : (
                  <span className="tw-font-bold">
                    {showTaskNumber ? `#${task.task_number}: ${task?.name}` : task?.name}
                  </span>
                )}
              </div>
              {showCancel && (
                <CancelCrossIcon
                  onClick={handleCancel}
                  height={12}
                  width={12}
                  className="tw-mx-2 tw-cursor-pointer tw-stroke-current tw-flex-shrink-0"
                />
              )}
            </div>
          )}
          <SelectDateNonWorkingDays
            selectedDays={dates}
            trigger={(text) => <SelectTrigger className="tw-w-auto">{text}</SelectTrigger>}
            selectionMode="daily"
            onSave={handleDateChange}
            required
            popperClassName="z-index-1300"
            backgroundElementClass="z-index-1300"
            defaultTimezoneAware
            type="selectable_global_day_off"
            weekendIsSelectable
            popperTooltipClassName="z-index-1300"
            disableDaysBefore={disableDaysBefore}
            position="bottom-end"
          />
        </div>
        {!hideFormElements ? (
          <>
            <Divider className="tw-my-5 tw--ml-[30px] tw--mr-[30px]" />
            <div className="tw-relative">
              {showAiInfoBox ? (
                <InfoBox
                  className="tw-mb-8 tw-px-5 tw-pt-4 tw-pb-3"
                  type="ai"
                >
                  <Body2 className="tw-mb-3">
                    <Trans>
                      ActiveCollab described and categorized the expense from your document. Happy with the results?
                      <br />
                      Give it a thumbs up.
                    </Trans>
                  </Body2>
                  <VoteThumbs id={promptId} />
                </InfoBox>
              ) : null}

              <div className="tw-flex tw-justify-between">
                <ExpenseFormContainer
                  projectId={project.id}
                  isBillable={isBillable}
                  onCancel={onClose}
                  users={filteredUsers}
                  defaultUserId={defaultUser.id}
                  defaultUserName={defaultUser.name}
                  recordDate={formatDates.to}
                  parentId={taskId > 0 ? taskId : projectId}
                  parentType={taskId > 0 ? "Task" : "Project"}
                  taskId={taskId > 0 ? taskId : null}
                  userId={loggedUser.id}
                  onDismiss={onClose}
                  categoryId={categoryId}
                  descriptionValue={descriptionValue}
                  initialValue={initialValue}
                  attachments={attachments}
                  displaySuccessToast={displaySuccessToast}
                />
              </div>
              <ExpenseParentTotalContainer
                parentId={taskId > 0 ? taskId : projectId}
                parentType={taskId > 0 ? "Task" : "Project"}
                projectId={project.id}
                onChangeExpand={handleToggleList}
                className="tw-absolute tw-right-0.5 tw-bottom-1"
              />
            </div>
            {showList ? (
              <ListGroupByDate
                parentId={taskId > 0 ? taskId : projectId}
                parentType={taskId > 0 ? "Task" : "Project"}
                projectId={project.id}
                className="tw-mt-6 tw-hidden sm:tw-block"
                withForm
              />
            ) : null}
          </>
        ) : null}
      </div>
    </ExpenseModalContainer>
  );
};

ExpenseModalInner.propTypes = {
  projectId: PropTypes.number.isRequired,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  taskId: PropTypes.number,
  categoryId: PropTypes.number,
  descriptionValue: PropTypes.string,
  initialValue: PropTypes.number,
  showAiInfoBox: PropTypes.bool,
  defaultDates: PropTypes.shape({
    from: PropTypes.number,
    to: PropTypes.number,
  }),
  attachments: PropTypes.shape({
    filesToAttach: PropTypes.array,
    filesToDrop: PropTypes.array,
  }),
  hideDropDown: PropTypes.bool,
  promptId: PropTypes.number,
  displaySuccessToast: PropTypes.bool,
};
